import { useState } from 'react';
import { useCalendarStore } from '@/store/calendarStore';
import { getDaysInMonth, getStartOfMonth, isSameDayAs, getDayName, parseDateFromBackend, formatDate } from '@/utils/dateUtils';
import { getDualDate } from '@/utils/hebrewCalendar';
import { showExportDialog } from '@/utils/exportUtils';
import ItemPopup from './ItemPopup';
import clsx from 'clsx';
import { FiCalendar, FiBook, FiStar, FiDownload } from 'react-icons/fi';

/**
 * Vista de mes del calendario
 */
export default function MonthView() {
  const { currentDate, setCurrentDate, citas, lecturas, eventos, settings, filters } = useCalendarStore();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  const days = getDaysInMonth(currentDate);
  const startOfMonth = getStartOfMonth(currentDate);
  const firstDayOfWeek = startOfMonth.getDay();
  
  // Ajustar según el primer día de la semana configurado
  const startPadding = (firstDayOfWeek - settings.firstDayOfWeek + 7) % 7;
  
  const today = new Date();

  // Obtener días de la semana
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const orderedWeekDays = [
    ...weekDays.slice(settings.firstDayOfWeek),
    ...weekDays.slice(0, settings.firstDayOfWeek)
  ];

  // Función para obtener items de un día ordenados por prioridad
  const getItemsForDay = (day) => {
    const dayCitas = filters.showCitas ? citas.filter(c => {
      try {
        const citaDate = parseDateFromBackend(c.fechaInicio);
        return isSameDayAs(citaDate, day);
      } catch {
        return false;
      }
    }) : [];

    const dayLecturas = filters.showLecturas ? lecturas.filter(l => {
      try {
        const lecturaDate = parseDateFromBackend(l.fecha);
        const isSameDay = isSameDayAs(lecturaDate, day);
        
        if (isSameDay && filters.tiposLectura.length > 0) {
          return filters.tiposLectura.includes(l.tipo);
        }
        
        return isSameDay;
      } catch {
        return false;
      }
    }) : [];

    const dayEventos = filters.showEventos ? eventos.filter(e => {
      try {
        const eventoDate = parseDateFromBackend(e.fechaInicio);
        return isSameDayAs(eventoDate, day);
      } catch {
        return false;
      }
    }) : [];

    // Ordenar por prioridad: Citas > Lecturas > Eventos
    return {
      citas: dayCitas,
      lecturas: dayLecturas,
      eventos: dayEventos,
      total: dayCitas.length + dayLecturas.length + dayEventos.length
    };
  };

  const handleDayClick = (day) => {
    setCurrentDate(day);
  };

  const handleItemClick = (e, item, type) => {
    e.stopPropagation();
    setSelectedItem(item);
    setSelectedItemType(type);
  };

  const closePopup = () => {
    setSelectedItem(null);
    setSelectedItemType(null);
  };

  // Función para verificar si una fecha está dentro de una cita multi-día
  const getActiveCitaForDay = (day) => {
    if (!filters.showCitas) return null;
    
    return citas.find(cita => {
      try {
        const inicio = parseDateFromBackend(cita.fechaInicio);
        const fin = parseDateFromBackend(cita.fechaFin);
        
        // Normalizar fechas a medianoche para comparación
        const diaActual = new Date(day.getFullYear(), day.getMonth(), day.getDate());
        const diaInicio = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
        const diaFin = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
        
        // Verificar si es una cita multi-día
        const esMultiDia = diaInicio.getTime() !== diaFin.getTime();
        
        // Verificar si el día actual está dentro del rango
        const estaDentroDelRango = diaActual >= diaInicio && diaActual <= diaFin;
        
        return esMultiDia && estaDentroDelRango;
      } catch {
        return false;
      }
    });
  };

  const renderDayItems = (items, maxItems = 10) => {
    const allItems = [];
    
    // Agregar citas primero
    items.citas.forEach(cita => {
      allItems.push({ ...cita, itemType: 'cita', icon: FiCalendar });
    });
    
    // Luego lecturas
    items.lecturas.forEach(lectura => {
      allItems.push({ ...lectura, itemType: 'lectura', icon: FiBook });
    });
    
    // Finalmente eventos
    items.eventos.forEach(evento => {
      allItems.push({ ...evento, itemType: 'evento', icon: FiStar });
    });

    const visibleItems = allItems.slice(0, maxItems);
    const hasMore = allItems.length > maxItems;

    return (
      <div className="mt-1 space-y-0.5 overflow-auto max-h-20">
        {visibleItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div
              key={`${item.itemType}-${idx}`}
              onClick={(e) => handleItemClick(e, item, item.itemType)}
              className="text-xs px-1 py-0.5 rounded flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity truncate"
              style={{ 
                backgroundColor: `${item.color}30`,
                borderLeft: `2px solid ${item.color}`
              }}
              title={item.nombre || item.tipo || item.referencia}
            >
              <Icon className="w-2.5 h-2.5 flex-shrink-0" style={{ color: item.color }} />
              <span className="truncate text-[10px]">
                {item.nombre || item.tipo || item.referencia?.split(' ').slice(0, 2).join(' ')}
              </span>
            </div>
          );
        })}
        {hasMore && (
          <div className="text-xs text-gray-500 text-center py-0.5">
            ...
          </div>
        )}
      </div>
    );
  };

  // Calcular total de eventos del mes
  const monthTotalCitas = days.reduce((sum, day) => sum + getItemsForDay(day).citas.length, 0);
  const monthTotalLecturas = days.reduce((sum, day) => sum + getItemsForDay(day).lecturas.length, 0);
  const monthTotalEventos = days.reduce((sum, day) => sum + getItemsForDay(day).eventos.length, 0);
  const monthTotalItems = monthTotalCitas + monthTotalLecturas + monthTotalEventos;

  // Función para exportar
  const handleExport = () => {
    const daysData = days.map(day => ({
      date: day,
      items: {
        citas: getItemsForDay(day).citas,
        lecturas: getItemsForDay(day).lecturas,
        eventos: getItemsForDay(day).eventos
      }
    }));
    
    const title = `Calendario - Vista Mes - ${formatDate(currentDate, 'MMMM yyyy')}`;
    showExportDialog(daysData, 'month', title);
  };

  return (
    <>
      <div className="bg-white shadow-md rounded-lg p-6 relative">
        {/* Botón de exportar */}
        <button
          onClick={handleExport}
          className="absolute top-4 right-4 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md z-10"
          title="Exportar mes"
        >
          <FiDownload className="w-4 h-4" />
          <span className="text-sm font-medium">Exportar</span>
        </button>

        {/* Encabezado de días de la semana */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {orderedWeekDays.map((day, idx) => (
            <div key={idx} className="text-center font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Grid de días */}
        <div className="grid grid-cols-7 gap-2 pb-20">
          {/* Espacios vacíos antes del primer día */}
          {Array.from({ length: startPadding }).map((_, idx) => (
            <div key={`empty-${idx}`} className="aspect-square"></div>
          ))}

          {/* Días del mes */}
          {days.map((day, idx) => {
            const isToday = isSameDayAs(day, today);
            const isSelected = isSameDayAs(day, currentDate);
            const items = getItemsForDay(day);
            const dualDate = getDualDate(day, settings.hebrewDayStart);
            const activeCita = getActiveCitaForDay(day);

            return (
              <div
                key={idx}
                onClick={() => handleDayClick(day)}
                className={clsx(
                  'aspect-square p-2 rounded-lg border-2 transition-all relative cursor-pointer overflow-hidden',
                  'hover:shadow-md hover:scale-105',
                  {
                    'border-primary bg-blue-50': isSelected,
                    'border-yellow-400 bg-yellow-50': isToday && !isSelected,
                    'border-gray-200 hover:border-gray-300': !isToday && !isSelected,
                  }
                )}
              >
                {/* Sombreado de hora hebrea (6 PM en adelante) - lado derecho */}
                <div 
                  className="absolute top-0 right-0 bottom-0 w-1/3 bg-gray-500 opacity-30 pointer-events-none"
                  title="Inicio del día hebreo (6:00 PM)"
                ></div>

                {/* Sombreado para citas ordenadas multi-día */}
                {activeCita && (
                  <div 
                    className="absolute inset-0 pointer-events-none z-[1]"
                    style={{ 
                      backgroundColor: activeCita.color,
                      opacity: 0.12
                    }}
                    title={`${activeCita.nombre} (en progreso)`}
                  ></div>
                )}

                <div className="relative flex flex-col h-full z-10">
                  {/* Número del día */}
                  <div className="font-semibold text-lg">
                    {day.getDate()}
                  </div>
                  
                  {/* Fecha hebrea */}
                  {settings.showDualCalendar && (
                    <div className="text-xs opacity-75">
                      {dualDate.hebrew.day} {dualDate.hebrew.monthName.substring(0, 3)}
                    </div>
                  )}

                  {/* Items del día */}
                  {items.total > 0 && renderDayItems(items)}

                  {/* Contador en esquina inferior izquierda */}
                  {items.total > 0 && (
                    <div className="absolute bottom-1 left-1">
                      <div className={clsx(
                        'text-[10px] px-1.5 py-0.5 rounded-full font-semibold shadow-sm',
                        isSelected 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-700 text-white'
                      )}>
                        {items.total}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Leyenda */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-yellow-400 bg-yellow-50"></div>
            <span>Hoy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-primary bg-blue-50"></div>
            <span>Seleccionado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-500 opacity-40 border-r-4 border-gray-500"></div>
            <span>Día hebreo (6 PM) →</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500 opacity-20"></div>
            <span>Cita multi-día activa</span>
          </div>
          <div className="flex items-center gap-2">
            <FiCalendar className="w-4 h-4" />
            <span>Citas</span>
          </div>
          <div className="flex items-center gap-2">
            <FiBook className="w-4 h-4" />
            <span>Lecturas</span>
          </div>
          <div className="flex items-center gap-2">
            <FiStar className="w-4 h-4" />
            <span>Eventos</span>
          </div>
        </div>

        {/* Contador de eventos totales del mes en esquina inferior izquierda */}
        {monthTotalItems > 0 && (
          <div className="absolute bottom-4 left-4 z-10">
            <div className="bg-gray-700 text-white px-3 py-2 rounded-lg shadow-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Total mes:</span>
                <span className="text-sm font-bold">{monthTotalItems}</span>
              </div>
              <div className="text-[10px] text-gray-300 mt-1">
                Citas: {monthTotalCitas} | Lecturas: {monthTotalLecturas} | Eventos: {monthTotalEventos}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Popup de detalles */}
      {selectedItem && (
        <ItemPopup 
          item={selectedItem}
          type={selectedItemType}
          onClose={closePopup}
        />
      )}
    </>
  );
}
